import { LOGISTICS_INVENTORY_ROUTE } from "../constants/resources";
import { formatCrmDateTime } from "./crm";

export const logisticsInventoryItemPath = (id: string): string =>
  `${LOGISTICS_INVENTORY_ROUTE}/${encodeURIComponent(id)}`;

// Inventory__c.Type__c in production (checked 2026-10-12). A type Salesforce
// adds later follows these, by name.
export const INVENTORY_TYPES = ["Tablet", "TV Box", "SIM", "Hardware", "Aktivitätstisch"];
export const SIM_TYPE = "SIM";

type Identifiers = {
  type?: string | null;
  imei?: string | null;
  serialNumber?: string | null;
  hardwareSerialNumber?: string | null;
  simIccid?: string | null;
};

// The identifier that fits the type: a SIM's ICCID; for a device its IMEI,
// else its serial number, else its hardware serial number.
export const inventoryIdentifier = (unit: Identifiers): string | null =>
  unit.type === SIM_TYPE
    ? (unit.simIccid ?? null)
    : (unit.imei ?? unit.serialNumber ?? unit.hardwareSerialNumber ?? unit.simIccid ?? null);

type Count = { type?: string | null; status?: string | null; count: number };

export type InventoryTypeCount = {
  type: string | null;
  total: number;
  statuses: { status: string | null; count: number }[];
};

const typeRank = (type: string | null): number => {
  if (type === null) return INVENTORY_TYPES.length + 1;
  const index = INVENTORY_TYPES.indexOf(type);
  return index === -1 ? INVENTORY_TYPES.length : index;
};

// The counts by type and status, grouped by type: the known types in their
// order, other types by name, units without a type last. A type's statuses
// come largest first.
export const groupInventoryCounts = (counts: Count[]): InventoryTypeCount[] => {
  const byType = new Map<string | null, InventoryTypeCount>();
  for (const { type = null, status = null, count } of counts) {
    const group = byType.get(type) ?? { type, total: 0, statuses: [] };
    group.total += count;
    group.statuses.push({ status, count });
    byType.set(type, group);
  }
  return Array.from(byType.values())
    .map(group => ({
      ...group,
      statuses: [...group.statuses].sort(
        (a, b) => b.count - a.count || (a.status ?? "").localeCompare(b.status ?? "", "de")
      ),
    }))
    .sort((a, b) => typeRank(a.type) - typeRank(b.type) || (a.type ?? "").localeCompare(b.type ?? "", "de"));
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

type SimProviderFacts = {
  connectivityStatus?: string | null;
  lastOnlineAt?: string | null;
};

// WhereverSIM's connectivity status of a SIM that is in a data session.
const CONNECTIVITY_ONLINE = "ONLINE";

// When the SIM was last online: now while WhereverSIM reports it online, else
// the time WhereverSIM last saw it online.
export const simLastOnlineText = (
  translate: Translate,
  { connectivityStatus, lastOnlineAt }: SimProviderFacts
): string => {
  if (connectivityStatus === CONNECTIVITY_ONLINE) {
    return translate("logistics.sim.onlineNow");
  }
  return lastOnlineAt ? formatCrmDateTime(lastOnlineAt) : translate("logistics.sim.lastOnlineUnknown");
};

type ProviderValue = string | number | boolean | null;

const isScalar = (value: unknown): value is ProviderValue =>
  value === null || ["string", "number", "boolean"].includes(typeof value);

const providerPath = (path: string, key: string | number): string => (path === "" ? `${key}` : `${path}.${key}`);

const flattenInto = (data: unknown, path: string, rows: [string, ProviderValue][]): void => {
  if (isScalar(data) || data === undefined) {
    if (path !== "") rows.push([path, data ?? null]);
  } else if (Array.isArray(data)) {
    if (data.every(isScalar) && data.length > 0) {
      rows.push([path, data.map(entry => (entry === null ? "–" : String(entry))).join(", ")]);
    } else if (data.length === 0) {
      rows.push([path, null]);
    } else {
      data.forEach((entry, index) => flattenInto(entry, providerPath(path, index), rows));
    }
  } else if (typeof data === "object") {
    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0 && path !== "") rows.push([path, null]);
    entries.forEach(([key, entry]) => flattenInto(entry, providerPath(path, key), rows));
  }
};

// What WhereverSIM returned as rows of a path and a value, in the order of the data: nested objects give
// dot paths (location.operator.name), a list of values one row joined with ", ", a list of objects one
// row per value under the index (services.0.name). An empty object or list is a row without a value.
export const flattenProviderData = (data: unknown): [path: string, value: ProviderValue][] => {
  const rows: [string, ProviderValue][] = [];
  flattenInto(data, "", rows);
  return rows;
};
